import { useContext } from 'react';
import {
  useSelectedWorkspace,
  useToken,
} from '../../Flow/ConsentContextProvider';
import { ReactMcpContext } from './McpContextProvider';
import {
  MCP_RESUME_FORM_FIELDS,
  MCP_SELECTED_TOOLSETS_SEPARATOR,
} from '../MCP/domain/contract';
import { buildMcpSelectedScopesForPost } from '../MCP/domain/scopeSelection';

const McpFlowParameters = () => {
  const mcpContext = useContext(ReactMcpContext);
  const { selectedWorkspace } = useSelectedWorkspace();
  const token = useToken();

  const selectedNames =
    mcpContext?.mcpSelectedToolsetNames ?? new Set<string>();
  const byIntent = mcpContext?.mcpByIntent ?? [];
  const allToolsets = byIntent.flatMap((g) => g.toolsets);

  // Only post names that are still in the effective toolset list
  const allNames = new Set(allToolsets.map((t) => t.name));
  const postedNames = [...selectedNames].filter((name) => allNames.has(name));

  const selectedScopesJoined = buildMcpSelectedScopesForPost(
    postedNames,
    allToolsets,
    token.scopes,
  ).join(' ');

  const selectedToolsetsJoined = postedNames.join(
    MCP_SELECTED_TOOLSETS_SEPARATOR,
  );
  // Only submit selections whose policy check completed successfully. Accept
  // is disabled on any error; this also keeps failed targets out of the POST as
  // a defense in depth.
  const selectedTrelloWorkspaceIds =
    mcpContext?.scopeClassification.hasTrello &&
    mcpContext.policies.trello.status === 'success'
      ? mcpContext.selectedTrelloWorkspaces
          .map((workspace) => workspace.id)
          .filter(Boolean)
      : [];
  const selectedBitbucketWorkspaceIds =
    mcpContext?.isBitbucketWorkspaceAccessEnabled &&
    mcpContext.policies.bitbucket.status === 'success'
      ? [mcpContext.selectedBitbucketWorkspace?.ari].filter(
          (ari): ari is string => ari != null,
        )
      : [];
  // A site and a Bitbucket workspace from different organizations cannot be
  // granted together, so no target is submitted while they disagree — the same
  // defense in depth as above. Accept is already disabled and the permissions
  // list withdrawn (see mcpAcceptDisabled / McpPermissions), so this only
  // matters if the form is ever submitted by some other path.
  const selectedWorkspaceIds = mcpContext?.hasOrgMismatch
    ? []
    : [
        ...(mcpContext?.scopeClassification.hasAtlassian &&
        mcpContext.policies.site.status === 'success' &&
        selectedWorkspace
          ? [selectedWorkspace]
          : []),
        ...selectedTrelloWorkspaceIds,
        ...selectedBitbucketWorkspaceIds,
      ];

  return (
    <>
      <input
        type="hidden"
        name={MCP_RESUME_FORM_FIELDS.selectedScopes}
        value={selectedScopesJoined}
      />
      <input
        type="hidden"
        name={MCP_RESUME_FORM_FIELDS.selectedToolsets}
        value={selectedToolsetsJoined}
      />
      {selectedWorkspaceIds.map((workspaceId) => (
        <input
          key={workspaceId}
          type="hidden"
          name="selectedWorkspace"
          value={workspaceId}
        />
      ))}
    </>
  );
};

export default McpFlowParameters;
